import { areTimeRangeInputValuesEqual } from '@chronosphereio/core';
import { useEffect, useRef } from 'react';
import type { InMemoryTimeRangeStorage } from './in-memory-time-range-storage';
import { usePinnedTimeRange, usePinnedTimeRangeStatus } from './pinned-time-range-hooks';
import type { UrlTimeRangeStorage } from './url-time-range-storage';

export type TimeRangeStorage = UrlTimeRangeStorage | InMemoryTimeRangeStorage;

/**
 * A hook that takes care of reconciling when the pinned time range differs from the provider's internal state. This
 * can happen in a number of situations like:
 *   - the time range is pinned and another tab that is also pinned updates the pinned time range value
 *   - the user uses the back / forward buttons while a time range is pinned so the URL and pinned value disagree
 */
export function useSyncPinnedTimeRange(storage: TimeRangeStorage, isEnabled: boolean) {
  const { timeRange, setTimeRange } = storage;

  const { pinnedTimeRange, setPinnedTimeRange } = usePinnedTimeRange();
  const { pinnedStatus } = usePinnedTimeRangeStatus();

  const previousState = useRef({
    pinnedTimeRange,
    pinnedStatus,
  });

  useEffect(() => {
    // See if we actually need to reconcile the time ranges not matching
    if (isEnabled === false || pinnedStatus !== 'pinned' || areTimeRangeInputValuesEqual(pinnedTimeRange, timeRange)) {
      previousState.current = { pinnedTimeRange, pinnedStatus };
      return;
    }

    if (
      previousState.current.pinnedStatus !== pinnedStatus ||
      previousState.current.pinnedTimeRange !== pinnedTimeRange
    ) {
      // If the pinned status or pinned time range changed, update ours (i.e. usually the URL) to match (e.g. this
      // covers updates to the pinned value from other tabs)
      setTimeRange(pinnedTimeRange, 'replaceIn');
    } else {
      // Otherwise, what's in the URL changed, probably due to the back / forward browser buttons being used, in which
      // case we want to remain pinned, but update the pinned time range to match the URL instead
      setPinnedTimeRange(timeRange);
    }

    previousState.current = { pinnedTimeRange, pinnedStatus };
  }, [isEnabled, pinnedStatus, pinnedTimeRange, setPinnedTimeRange, setTimeRange, timeRange]);
}
